// Safari-specific optimizations and utilities

/**
 * Detect if the current browser is Safari
 */
export const isSafari = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

/**
 * Safari-specific performance optimizations
 */
export const applySafariOptimizations = (): void => {
  if (typeof window === 'undefined') return;
  
  if (isSafari()) {
    // Apply Safari-specific optimizations
    (document.body.style as any).webkitOverflowScrolling = 'touch';
    (document.body.style as any).webkitTransform = 'translateZ(0)';
    
    // Optimize scroll behavior
    document.documentElement.style.scrollBehavior = 'auto';
    
    // Reduce paint complexity
    const style = document.createElement('style');
    style.textContent = `
      * {
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
      }
      
      .MuiTable-root,
      .MuiTableRow-root,
      .MuiTableCell-root {
        contain: layout style paint;
        -webkit-transform: translateZ(0);
        transform: translateZ(0);
      }
    `;
    document.head.appendChild(style);
  }
};

/**
 * Safari-optimized debounce function
 */
export const safariDebounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

/**
 * Safari-optimized throttle function
 */
export const safariThrottle = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let lastCall = 0;
  
  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      func(...args);
    }
  };
};

/**
 * Safari-optimized array processing
 */
export const safariOptimizedArrayProcessing = <T>(
  array: T[],
  processor: (item: T, index: number) => any,
  batchSize: number = 50
): any[] => {
  if (!isSafari()) {
    return array.map(processor);
  }
  
  // Safari-optimized batch processing
  const result: any[] = [];
  for (let i = 0; i < array.length; i += batchSize) {
    const batch = array.slice(i, i + batchSize);
    result.push(...batch.map(processor));
    
    // Yield to browser for better performance
    if (i % (batchSize * 4) === 0) {
      // Small delay to prevent blocking
      new Promise(resolve => setTimeout(resolve, 0));
    }
  }
  
  return result;
};

/**
 * Safari-optimized DOM manipulation
 */
export const safariOptimizedDOMUpdate = (
  updateFn: () => void,
  useRAF: boolean = true
): void => {
  if (!isSafari()) {
    updateFn();
    return;
  }
  
  if (useRAF && typeof requestAnimationFrame !== 'undefined') {
    requestAnimationFrame(updateFn);
  } else {
    // Fallback with minimal delay
    setTimeout(updateFn, 0);
  }
};

/**
 * Safari-optimized memory management
 */
export const safariMemoryOptimization = (): void => {
  if (typeof window === 'undefined' || !isSafari()) return;
  
  // Clear unused object URLs
  const cleanupObjectURLs = () => {
    const urls = (window as any).__objectURLs || [];
    urls.forEach((url: string) => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        // Ignore errors
      }
    });
    (window as any).__objectURLs = [];
  };
  
  // Clean up every 30 seconds
  setInterval(cleanupObjectURLs, 30000);
  
  // Clean up on page unload
  window.addEventListener('beforeunload', cleanupObjectURLs);
};

/**
 * Initialize Safari optimizations
 */
export const initializeSafariOptimizations = (): void => {
  if (typeof window === 'undefined') return;
  
  applySafariOptimizations();
  safariMemoryOptimization();
  
  // Add Safari-specific CSS classes
  if (isSafari()) {
    document.documentElement.classList.add('safari-optimized');
  }
};
